// Tela pública do convidado — Compartilhar localização. Aberta pelo link
// gerado em location-share-sheet.tsx, SEM login: o token na URL é a única
// credencial. Rota pública de propósito (bypass de AuthProvider/AuthGate em
// __root.tsx, mesmo caso de /contrato/assinar/:token) — por isso nada de
// React Query aqui, só fetch direto pelo service com polling próprio.
//
// Refeita 2026-09-23 (pedido: "esse compartilhar link da localização é mais
// para tático buscar o veículo [...] tem que ser algo mais sério quando
// precisar de guincho ou equipe de busca"): visual operacional, sem
// confete/emoji, e navegação DENTRO da tela — rota do GPS do convidado até
// o veículo, recalculada sozinha quando ele sai da rota ou o veículo anda.
// A rota vem do backend (Google Routes com trânsito; OSRM de reserva).
//
// Tudo que aparece além do ponto no mapa depende do que o dono liberou
// (permissions) — o backend já nem manda o que não foi liberado.
import { createFileRoute } from "@tanstack/react-router";
import { POLITRACKER_TINTS } from "@/lib/vehicle-icons/colors";
import { isoFrameFor, resolveIsoSheet } from "@/lib/vehicle-icons/photo-registry";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Polyline as GooglePolyline,
  useMap as useGoogleMap,
} from "@vis.gl/react-google-maps";
import {
  ArrowUp,
  ArrowUpLeft,
  ArrowUpRight,
  BarChart3,
  Check,
  Clock,
  Copy,
  CornerUpLeft,
  CornerUpRight,
  Crosshair,
  ExternalLink,
  Flag,
  Layers,
  Lock,
  LockOpen,
  PersonStanding,
  LoaderCircle,
  LocateFixed,
  MapPin,
  Maximize2,
  Navigation,
  RotateCw,
  Route as RouteIcon,
  ShieldCheck,
  TriangleAlert,
  Undo2,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import {
  gpsDriveLocationShareService,
  formatRemaining,
  type LocationShareRoute,
  type LocationShareRouteStep,
  type PublicLocationShare,
} from "@/services/gpsdrive-location-share.service";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { GOOGLE_MAPS_CLEAN_STYLE, hasCustomGoogleMapId, resolveGoogleMapId } from "@/lib/google-maps-style";
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";
import { LicensePlate } from "@/components/license-plate";
import { StreetViewPanorama } from "@/components/street-view-panorama";
import { SatelliteZoomGuard } from "@/components/satellite-zoom-guard";

export const Route = createFileRoute("/ao-vivo/$token")({
  head: () => ({
    meta: [
      { title: "Localização em tempo real — GPS Drive" },
      { property: "og:title", content: "Localização do veículo em tempo real" },
      {
        property: "og:description",
        content: "Acompanhe a posição do veículo ao vivo e trace a rota até ele. GPS Drive — Mais segurança, mais controle, em tempo real.",
      },
      { name: "theme-color", content: "#020617" },
    ],
  }),
  component: LiveSharePage,
});

const POLL_MS = 10_000;
// Durante a navegação a posição do veículo importa mais (ele pode estar
// andando) — poll mais curto.
const POLL_NAV_MS = 5_000;
// Trajeto (relatório de rota no Traccar) é caro — só na abertura e a cada
// ~1 min; entre um e outro, as posições ao vivo vão sendo emendadas aqui.
// Pedido SEPARADO da posição (lentidão real 2026-10-28: com o Traccar
// ocupado o trajeto levou 34s e a tela ficava só no "carregando").
const STALE_MS = 10 * 60_000;

// Navegação
const ROUTE_MIN_INTERVAL_MS = 8_000;
const ROUTE_RETRY_AFTER_ERROR_MS = 15_000;
const ROUTE_MAX_AGE_MS = 120_000;
const OFF_ROUTE_MIN_M = 45;
const DEST_MOVED_M = 60;
const ARRIVED_M = 30;

const BRAND_PHRASE = ["Mais segurança", "Mais controle", "Em tempo real"] as const;

type LatLng = { lat: number; lng: number };
type GuestFix = LatLng & { accuracy: number; heading: number | null; at: number };
type CameraMode = "vehicle" | "me" | "overview" | "free";

const MAP_TYPES = [
  { key: "roadmap", label: "Mapa" },
  { key: "hybrid", label: "Satélite" },
  { key: "terrain", label: "Terreno" },
] as const;
type MapTypeKey = (typeof MAP_TYPES)[number]["key"];
const MAP_TYPE_STORAGE_KEY = "gpsdrive:ao-vivo-map-type";
const GUARD_MOD = { useMap: useGoogleMap };

const KEYFRAMES = `
@keyframes avPulse { 0% { transform: scale(.6); opacity: .6 } 100% { transform: scale(2.4); opacity: 0 } }
@keyframes avBlink { 0%,100% { opacity: 1 } 50% { opacity: .3 } }
@keyframes avSlideUp { 0% { transform: translateY(24px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
@keyframes avSlideDown { 0% { transform: translateY(-24px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
@keyframes avScan { 0% { transform: rotate(0) } 100% { transform: rotate(360deg) } }
@keyframes avBar { 0% { transform: translateX(-100%) } 100% { transform: translateX(250%) } }
`;

function LiveSharePage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<PublicLocationShare | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [netError, setNetError] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState<number>(0);
  const [navigating, setNavigating] = useState(false);
  const now = useNow(1000);

  const load = useCallback(
    async (first: boolean) => {
      if (!first && typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await gpsDriveLocationShareService.getPublic(token, { trail: false, view: first });
        setNetError(false);
        setLastFetchAt(Date.now());
        setData(res);
      } catch (err) {
        if (err instanceof GpsDriveApiError && err.status === 404) setNotFound(true);
        else setNetError(true);
      }
    },
    [token],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const status = data
    ? data.status === "active" && new Date(data.expiresAt).getTime() <= now
      ? "expired"
      : data.status
    : null;

  useEffect(() => {
    if (status !== "active") return;
    const t = setInterval(() => void load(false), navigating ? POLL_NAV_MS : POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void load(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, load, navigating]);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-slate-950 font-sans text-white">
      <style>{KEYFRAMES}</style>
      {notFound ? (
        <EndScreen
          icon={<TriangleAlert className="h-9 w-9 text-amber-400" />}
          title="Link inválido"
          text="Este link de localização não existe ou está incompleto. Solicite um novo link a quem compartilhou."
        />
      ) : !data ? (
        <LoadingScreen error={netError} onRetry={() => void load(true)} />
      ) : status === "expired" ? (
        <EndScreen
          icon={<Clock className="h-9 w-9 text-slate-300" />}
          title="Link expirado"
          text={`O prazo deste compartilhamento terminou. A localização não está mais disponível${data.sharerName ? ` — solicite um novo link a ${data.sharerName}` : ""}.`}
        />
      ) : status === "revoked" ? (
        <EndScreen
          icon={<ShieldCheck className="h-9 w-9 text-slate-300" />}
          title="Compartilhamento encerrado"
          text={`${data.sharerName ?? "Quem compartilhou"} encerrou o acesso. A localização não está mais visível.`}
        />
      ) : (
        <LiveView
          token={token}
          data={data}
          now={now}
          lastFetchAt={lastFetchAt}
          netError={netError}
          navigating={navigating}
          setNavigating={setNavigating}
        />
      )}
    </div>
  );
}

function LiveView({
  token,
  data,
  now,
  lastFetchAt,
  netError,
  navigating,
  setNavigating,
}: {
  token: string;
  data: PublicLocationShare;
  now: number;
  lastFetchAt: number;
  netError: boolean;
  navigating: boolean;
  setNavigating: (v: boolean) => void;
}) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  const target = useMemo(
    () => (data.position ? { lat: data.position.lat, lng: data.position.lng } : null),
    [data.position?.lat, data.position?.lng], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const animated = useTweenedPosition(target);
  const [camera, setCamera] = useState<{ mode: CameraMode; seq: number }>({ mode: "vehicle", seq: 0 });
  const setMode = useCallback((mode: CameraMode) => setCamera((c) => ({ mode, seq: c.seq + 1 })), []);
  const [mapType, setMapTypeState] = useState<MapTypeKey>(() => {
    try {
      const v = localStorage.getItem(MAP_TYPE_STORAGE_KEY);
      return MAP_TYPES.some((t) => t.key === v) ? (v as MapTypeKey) : "roadmap";
    } catch {
      return "roadmap";
    }
  });
  const setMapType = (t: MapTypeKey) => {
    setMapTypeState(t);
    try {
      localStorage.setItem(MAP_TYPE_STORAGE_KEY, t);
    } catch {
      /* sem armazenamento — vale só nesta visita */
    }
  };
  const [layersOpen, setLayersOpen] = useState(false);
  const [streetViewOpen, setStreetViewOpen] = useState(false);

  // GPS do convidado: liga no "Traçar rota" (navegação) ou no "Minha
  // distância" — nunca pede permissão sozinho na abertura.
  const [wantGps, setWantGps] = useState(false);
  const gps = useGuestGps(wantGps || navigating);
  const nav = useNavigation(token, navigating, gps.fix, target);
  const [voiceOn, setVoiceOn] = useState(true);
  useVoiceGuidance(navigating && voiceOn, nav);
  useWakeLock(navigating);

  // Primeira rota: mostra ela inteira, depois passa a seguir o convidado.
  const hadRoute = useRef(false);
  useEffect(() => {
    if (!navigating) {
      hadRoute.current = false;
      return;
    }
    if (nav.route && !hadRoute.current) {
      hadRoute.current = true;
      setMode("overview");
      const t = setTimeout(() => setMode("me"), 2500);
      return () => clearTimeout(t);
    }
  }, [navigating, nav.route, setMode]);

  const startNav = () => {
    setNavigating(true);
  };
  const stopNav = () => {
    setNavigating(false);
    setMode("vehicle");
  };

  const distanceM = gps.fix && target ? metersBetween(gps.fix, target) : null;
  const remainingPath = nav.route && nav.progress ? remainingGeometry(nav.route, nav.progress) : null;
  const guestHeading =
    gps.fix?.heading ??
    (navigating && remainingPath ? routeBearing(remainingPath) : null);

  return (
    <>
      <div className="absolute inset-0">
        {target ? (
          <APIProvider apiKey={apiKey}>
            <GoogleMap
              mapId={resolveGoogleMapId()}
              styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
              defaultCenter={target}
              defaultZoom={16}
              mapTypeId={mapType}
              disableDefaultUI
              clickableIcons={false}
              gestureHandling="greedy"
              style={{ height: "100%", width: "100%" }}
            >
              {/* Sem o rastro amarelo (2026-10-30): ligava os pontos em reta,
                  cortando quadras — bug visual em todas as empresas. */}
              {navigating && remainingPath && remainingPath.length > 1 && (
                <>
                  <GooglePolyline path={remainingPath} strokeColor="#1e3a8a" strokeWeight={11} strokeOpacity={0.9} zIndex={3} />
                  <GooglePolyline path={remainingPath} strokeColor="#3b82f6" strokeWeight={7} strokeOpacity={1} zIndex={4} />
                </>
              )}
              {gps.fix && (
                <AdvancedMarker position={gps.fix} zIndex={6}>
                  <GuestMarker heading={guestHeading} />
                </AdvancedMarker>
              )}
              {animated && (
                <AdvancedMarker position={animated} zIndex={10}>
                  {data.mapOnly ? <RealisticVehicleMarker data={data} now={now} /> : <VehicleMarker data={data} now={now} />}
                </AdvancedMarker>
              )}
              <SatelliteZoomGuard Mod={GUARD_MOD} mapTypeId={mapType} />
              <CameraController
                mode={camera.mode}
                seq={camera.seq}
                vehicle={animated}
                me={gps.fix}
                route={nav.route}
                navigating={navigating}
                onModeChange={setMode}
              />
            </GoogleMap>
          </APIProvider>
        ) : (
          <NoPositionYet />
        )}
      </div>

      {navigating ? <NavBanner nav={nav} gpsError={gps.error} /> : <TopBar data={data} now={now} />}

      {/* Controles do mapa */}
      {target && (
        <div
          className="absolute right-3 z-20 flex flex-col gap-2"
          style={{ top: navigating ? "calc(10rem + env(safe-area-inset-top))" : "calc(8.25rem + env(safe-area-inset-top))" }}
        >
          <MapButton active={camera.mode === "vehicle"} onClick={() => setMode("vehicle")} label="Centralizar no veículo">
            <Crosshair className="h-5 w-5" />
          </MapButton>
          <div className="relative">
            <MapButton active={layersOpen || mapType !== "roadmap"} onClick={() => setLayersOpen((o) => !o)} label="Tipo de mapa">
              <Layers className="h-5 w-5" />
            </MapButton>
            {layersOpen && (
              <div className="absolute right-full top-0 mr-2 flex overflow-hidden rounded-xl bg-slate-950/95 shadow-lg ring-1 ring-white/10">
                {MAP_TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      setMapType(t.key);
                      setLayersOpen(false);
                    }}
                    className={`h-11 whitespace-nowrap px-3 text-[13px] font-semibold ${
                      mapType === t.key ? "bg-blue-600 text-white" : "text-slate-200 active:bg-white/10"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!navigating && (
            <MapButton active={false} onClick={() => setStreetViewOpen(true)} label="Street View">
              <PersonStanding className="h-5 w-5" />
            </MapButton>
          )}
          {gps.fix && (
            <>
              <MapButton active={camera.mode === "me"} onClick={() => setMode("me")} label="Minha posição">
                <LocateFixed className="h-5 w-5" />
              </MapButton>
              <MapButton active={camera.mode === "overview"} onClick={() => setMode("overview")} label="Ver tudo">
                <Maximize2 className="h-5 w-5" />
              </MapButton>
            </>
          )}
          {navigating && (
            <MapButton active={voiceOn} onClick={() => setVoiceOn((v) => !v)} label={voiceOn ? "Desligar voz" : "Ligar voz"}>
              {voiceOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </MapButton>
          )}
        </div>
      )}

      {streetViewOpen && animated && (
        <StreetViewOverlay
          lat={animated.lat}
          lng={animated.lng}
          heading={data.position?.course}
          onClose={() => setStreetViewOpen(false)}
        />
      )}

      {navigating ? (
        <NavBottom nav={nav} data={data} now={now} onStop={stopNav} />
      ) : data.mapOnly ? null : (
        <BottomPanel
          token={token}
          data={data}
          now={now}
          lastFetchAt={lastFetchAt}
          netError={netError}
          distanceM={distanceM}
          gpsLocating={gps.locating}
          gpsError={gps.error}
          onAskDistance={() => {
            setWantGps(true);
            if (gps.fix) setMode("overview");
          }}
          onStartNav={startNav}
        />
      )}
    </>
  );
}

// --- Topo -------------------------------------------------------------------

function TopBar({ data, now }: { data: PublicLocationShare; now: number }) {
  const start = new Date(data.startsAt).getTime();
  const end = new Date(data.expiresAt).getTime();
  const remaining = end - now;
  const frac = Math.max(0, Math.min(1, remaining / Math.max(1, end - start)));
  const urgent = remaining < 5 * 60_000;

  return (
    <div
      className="absolute inset-x-3 z-20"
      style={{ top: "calc(0.75rem + env(safe-area-inset-top))", animation: "avSlideDown .4s ease-out both" }}
    >
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-950/90 shadow-2xl backdrop-blur">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <img src={defaultLogoUrl} alt="GPS Drive" className="h-7 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold uppercase tracking-wide text-white">Localização em tempo real</div>
            <div className="truncate text-[11px] text-slate-400">
              {data.sharerName ? `Compartilhado por ${data.sharerName}` : "Link de acompanhamento"}
              {data.guestName ? ` · para ${data.guestName}` : ""}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="flex items-center justify-end gap-1.5 text-[11px] font-bold uppercase tracking-wider text-red-400">
              <span className="h-2 w-2 rounded-full bg-red-500" style={{ animation: "avBlink 1.2s ease-in-out infinite" }} />
              Ao vivo
            </div>
            <div className={`mt-0.5 text-[11px] tabular-nums ${urgent ? "font-bold text-red-400" : "text-slate-400"}`}>
              expira em {formatRemaining(remaining)}
            </div>
          </div>
        </div>
        <div className="h-[3px] w-full bg-white/5">
          <div
            className={`h-full ${urgent ? "bg-red-500" : "bg-emerald-500"}`}
            style={{ width: `${frac * 100}%`, transition: "width 1s linear" }}
          />
        </div>
        <BrandPhrase className="border-t border-white/5 px-3 py-1.5" />
      </div>
      {data.message && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-slate-950/90 px-3 py-2 text-[13px] text-amber-100 shadow-lg backdrop-blur">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <span>{data.message}</span>
        </div>
      )}
    </div>
  );
}

function BrandPhrase({ className = "" }: { className?: string }) {
  const icons = [ShieldCheck, BarChart3, Clock];
  return (
    <div className={`flex items-center justify-around text-[10.5px] font-medium text-slate-300 ${className}`}>
      {BRAND_PHRASE.map((t, i) => {
        const Icon = icons[i];
        return (
          <span key={t} className="flex items-center gap-1">
            <Icon className="h-3 w-3 text-[#008CFF]" />
            {t}
          </span>
        );
      })}
    </div>
  );
}

// --- Painel inferior (acompanhamento) ----------------------------------------

function vehicleStatus(data: PublicLocationShare, now: number) {
  const pos = data.position;
  if (!pos) return { label: "Aguardando sinal", tone: "slate" as const };
  const age = now - new Date(pos.fixTime).getTime();
  if (age > STALE_MS) return { label: "Sem comunicação recente", tone: "amber" as const };
  if (pos.moving) return { label: "Em movimento", tone: "emerald" as const };
  if (pos.ignition === true) return { label: "Parado · ignição ligada", tone: "sky" as const };
  if (pos.ignition === false) return { label: "Parado · ignição desligada", tone: "slate" as const };
  return { label: "Parado", tone: "slate" as const };
}

const TONE: Record<"slate" | "amber" | "emerald" | "sky", string> = {
  slate: "bg-slate-500/20 text-slate-200 ring-slate-400/30",
  amber: "bg-amber-500/15 text-amber-300 ring-amber-400/40",
  emerald: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/40",
  sky: "bg-sky-500/15 text-sky-300 ring-sky-400/40",
};

function BottomPanel({
  token,
  data,
  now,
  lastFetchAt,
  netError,
  distanceM,
  gpsLocating,
  gpsError,
  onAskDistance,
  onStartNav,
}: {
  token: string;
  data: PublicLocationShare;
  now: number;
  lastFetchAt: number;
  netError: boolean;
  distanceM: number | null;
  gpsLocating: boolean;
  gpsError: string | null;
  onAskDistance: () => void;
  onStartNav: () => void;
}) {
  const pos = data.position;
  const fixAge = pos ? now - new Date(pos.fixTime).getTime() : Infinity;
  const st = vehicleStatus(data, now);
  const speed = useTweenedNumber(pos?.speedKmh ?? 0);
  const v = data.vehicle;
  const secondsAgo = lastFetchAt ? Math.max(0, Math.round((now - lastFetchAt) / 1000)) : null;
  const photo = v?.photoUrl ? resolveGpsDriveUrl(v.photoUrl) : null;
  const [copied, setCopied] = useState(false);
  const [showExternal, setShowExternal] = useState(false);

  const copyCoords = async () => {
    if (!pos) return;
    try {
      await navigator.clipboard.writeText(`${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard bloqueado — coordenadas continuam visíveis na tela */
    }
  };

  return (
    <div
      className="absolute inset-x-3 z-20"
      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))", animation: "avSlideUp .45s ease-out both" }}
    >
      <div className="max-h-[62dvh] overflow-y-auto rounded-2xl border border-white/10 bg-slate-950/95 p-3.5 shadow-2xl backdrop-blur">
        {/* Veículo + status */}
        <div className="flex items-center gap-3">
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-800 ring-1 ring-white/10">
            {photo ? (
              <img src={photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center">
                <Navigation className="h-6 w-6 text-slate-400" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {v?.plate && <Plate plate={v.plate} />}
              <span className="truncate text-[15px] font-bold">{v?.name ?? "Veículo"}</span>
            </div>
            {(v?.model || v?.color) && (
              <div className="mt-0.5 truncate text-xs text-slate-400">{[v?.model, v?.color].filter(Boolean).join(" · ")}</div>
            )}
            <span className={`mt-1 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${TONE[st.tone]}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {st.label}
            </span>
          </div>
          {data.permissions.showSpeed && pos && pos.speedKmh != null && (
            <div className="shrink-0 rounded-xl bg-white/5 px-3 py-2 text-center ring-1 ring-white/10">
              <div className="text-2xl font-black tabular-nums leading-none">{Math.round(speed)}</div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">km/h</div>
            </div>
          )}
        </div>

        {/* Dados operacionais */}
        {pos && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <InfoCell label="Última posição">
              <span className="tabular-nums">{formatClock(new Date(pos.fixTime))}</span>
              <span className={fixAge > STALE_MS ? "text-amber-400" : "text-slate-400"}> · {formatAgo(fixAge)}</span>
            </InfoCell>
            <InfoCell label="Distância até você">
              {distanceM != null ? (
                <span className="font-semibold">{formatDistance(distanceM)} <span className="font-normal text-slate-400">em linha reta</span></span>
              ) : gpsLocating ? (
                <span className="text-slate-400">Obtendo seu GPS…</span>
              ) : (
                <button type="button" onClick={onAskDistance} className="font-semibold text-sky-400 underline-offset-2 hover:underline">
                  Calcular
                </button>
              )}
            </InfoCell>
            <button
              type="button"
              onClick={copyCoords}
              className="col-span-2 flex items-center justify-between gap-2 rounded-lg bg-white/5 px-2.5 py-2 text-left ring-1 ring-white/10 active:bg-white/10"
            >
              <span className="min-w-0">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Coordenadas</span>
                <span className="font-mono text-[12.5px] tabular-nums text-slate-100">
                  {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-sky-400">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </span>
            </button>
            {data.address && (
              <div className="col-span-2 flex items-start gap-2 rounded-lg bg-white/5 px-2.5 py-2 ring-1 ring-white/10">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />
                <span className="line-clamp-2 text-slate-200">{data.address}</span>
              </div>
            )}
          </div>
        )}

        {gpsError && <div className="mt-2 text-[11px] text-amber-400">{gpsError}</div>}

        {data.permissions.allowBlock && pos && <BlockControls token={token} data={data} />}

        {/* Ações */}
        {data.permissions.allowDirections && pos ? (
          <div className="mt-3">
            <button
              type="button"
              onClick={onStartNav}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-blue-600/30 active:bg-blue-700"
            >
              <RouteIcon className="h-5 w-5" /> Traçar rota até o veículo
            </button>
            <div className="mt-1.5 text-center">
              <button
                type="button"
                onClick={() => setShowExternal((s) => !s)}
                className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:underline"
              >
                Abrir em outro aplicativo
              </button>
            </div>
            {showExternal && (
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <a
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-white/5 py-2 text-xs font-semibold ring-1 ring-white/10"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${pos.lat},${pos.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Maps <ExternalLink className="h-3 w-3" />
                </a>
                <a
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-white/5 py-2 text-xs font-semibold ring-1 ring-white/10"
                  href={`https://waze.com/ul?ll=${pos.lat},${pos.lng}&navigate=yes`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Waze <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        ) : pos ? (
          <div className="mt-3 rounded-lg bg-white/5 py-2 text-center text-[11px] text-slate-400 ring-1 ring-white/10">
            Link somente para visualização
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-2 text-[10.5px] text-slate-500">
          <span className="flex items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full ${netError ? "bg-amber-400" : "bg-emerald-400"}`}
              style={{ animation: "avBlink 1.4s ease-in-out infinite" }}
            />
            {netError ? "Reconectando…" : secondsAgo != null ? `Atualizado há ${secondsAgo}s` : "Conectado"}
          </span>
          <span>GPS Drive · Mais segurança, mais controle</span>
        </div>
      </div>
    </div>
  );
}

// Bloquear/desbloquear pelo link (pedido 2026-10-28) — só aparece quando
// quem compartilhou liberou. Confirmação na própria tela (não popup), o
// backend age em nome de quem criou o link e grava na aba LOGS.
function BlockControls({ token, data }: { token: string; data: PublicLocationShare }) {
  const pos = data.position;
  const [confirm, setConfirm] = useState<"engineStop" | "engineResume" | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [expected, setExpected] = useState<boolean | null>(null);
  const blocked = pos?.blocked ?? null;

  // Rastreador confirmou o estado pedido — tira o "aguardando".
  useEffect(() => {
    if (expected != null && blocked === expected) {
      setExpected(null);
      setResult({ ok: true, text: expected ? "Veículo bloqueado." : "Veículo desbloqueado." });
    }
  }, [blocked, expected]);

  const send = async (type: "engineStop" | "engineResume") => {
    setSending(true);
    setResult(null);
    try {
      await gpsDriveLocationShareService.sendPublicCommand(token, type);
      setExpected(type === "engineStop");
      setResult({ ok: true, text: "Comando enviado. Aguardando o rastreador confirmar…" });
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error && err.message ? err.message : "Não foi possível enviar o comando." });
    } finally {
      setSending(false);
      setConfirm(null);
    }
  };

  return (
    <div className="mt-3 rounded-xl bg-white/5 p-2.5 ring-1 ring-white/10">
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold uppercase tracking-wider text-slate-500">Bloqueio</span>
        <span
          className={`font-bold uppercase tracking-wide ${
            blocked === true ? "text-red-400" : blocked === false ? "text-emerald-400" : "text-slate-400"
          }`}
        >
          {blocked === true ? "Bloqueado" : blocked === false ? "Desbloqueado" : "Situação desconhecida"}
        </span>
      </div>
      {confirm ? (
        <div>
          <div className="mb-2 text-center text-[13px] font-semibold text-white">
            {confirm === "engineStop" ? "Confirmar BLOQUEIO do veículo?" : "Confirmar DESBLOQUEIO do veículo?"}
          </div>
          {confirm === "engineStop" && pos?.moving && (
            <div className="mb-2 text-center text-[11px] text-amber-400">Atenção: o veículo está em movimento.</div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={sending}
              onClick={() => setConfirm(null)}
              className="rounded-lg bg-white/10 py-2.5 text-sm font-semibold text-slate-200 active:bg-white/20"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={sending}
              onClick={() => void send(confirm)}
              className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-bold text-white ${
                confirm === "engineStop" ? "bg-red-600 active:bg-red-700" : "bg-emerald-600 active:bg-emerald-700"
              }`}
            >
              {sending && <LoaderCircle className="h-4 w-4 animate-spin" />}
              Confirmar
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setConfirm("engineStop")}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-red-600/90 py-2.5 text-sm font-bold text-white active:bg-red-700"
          >
            <Lock className="h-4 w-4" /> Bloquear
          </button>
          <button
            type="button"
            onClick={() => setConfirm("engineResume")}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600/90 py-2.5 text-sm font-bold text-white active:bg-emerald-700"
          >
            <LockOpen className="h-4 w-4" /> Desbloquear
          </button>
        </div>
      )}
      {result && (
        <div className={`mt-2 text-center text-[11.5px] ${result.ok ? "text-emerald-300" : "text-red-400"}`}>{result.text}</div>
      )}
    </div>
  );
}

function StreetViewOverlay({
  lat,
  lng,
  heading,
  onClose,
}: {
  lat: number;
  lng: number;
  heading?: number;
  onClose: () => void;
}) {
  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-950">
      <div className="relative min-h-0 flex-1">
        <StreetViewPanorama
          lat={lat}
          lng={lng}
          heading={heading}
          className="h-full w-full"
          fallback={
            <div className="grid h-full w-full place-items-center px-6 text-center text-sm text-slate-400">
              Sem imagem do Street View neste ponto.
            </div>
          }
        />
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex items-center justify-center gap-2 bg-slate-900 py-4 text-[15px] font-bold text-white ring-1 ring-white/10 active:bg-slate-800"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <Undo2 className="h-5 w-5" /> Voltar ao mapa
      </button>
    </div>
  );
}

function InfoCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-white/5 px-2.5 py-2 ring-1 ring-white/10">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-100">{children}</div>
    </div>
  );
}

function Plate({ plate }: { plate: string }) {
  return <LicensePlate plate={plate} size="sm" />;
}

function MapButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`grid h-11 w-11 place-items-center rounded-xl shadow-lg ring-1 transition ${
        active ? "bg-blue-600 text-white ring-blue-400/50" : "bg-slate-950/90 text-slate-200 ring-white/10"
      }`}
    >
      {children}
    </button>
  );
}

// --- Navegação --------------------------------------------------------------

type NavState = ReturnType<typeof useNavigation>;

function NavBanner({ nav, gpsError }: { nav: NavState; gpsError: string | null }) {
  const next = nav.nextStep;
  const after = nav.afterStep;
  let body: ReactNode;
  if (gpsError) {
    body = <NavMessage icon={<TriangleAlert className="h-7 w-7" />} title="GPS indisponível" text={gpsError} tone="amber" />;
  } else if (nav.arrived) {
    body = <NavMessage icon={<Flag className="h-7 w-7" />} title="Você chegou ao veículo" text="O veículo está a menos de 30 m de você." tone="emerald" />;
  } else if (!nav.route) {
    body = nav.error ? (
      <NavMessage icon={<TriangleAlert className="h-7 w-7" />} title="Sem rota" text={nav.error} tone="amber" />
    ) : (
      <NavMessage
        icon={<LoaderCircle className="h-7 w-7" style={{ animation: "avScan 1s linear infinite" }} />}
        title={nav.hasFix ? "Calculando rota…" : "Obtendo sua localização…"}
        text={nav.hasFix ? "Traçando o melhor caminho até o veículo." : "Permita o acesso à localização no navegador."}
        tone="blue"
      />
    );
  } else if (next) {
    body = (
      <div className="flex items-center gap-3">
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-white/15">
          <ManeuverIcon step={next} className="h-9 w-9" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[26px] font-black tabular-nums leading-none">{formatDistance(nav.distToNextM ?? 0)}</div>
          <div className="mt-1 line-clamp-2 text-[15px] font-semibold leading-snug">{next.instruction}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-x-3 z-20" style={{ top: "calc(0.75rem + env(safe-area-inset-top))", animation: "avSlideDown .3s ease-out both" }}>
      <div className="overflow-hidden rounded-2xl bg-blue-700 shadow-2xl ring-1 ring-blue-400/40">
        <div className="px-3.5 py-3">{body}</div>
        {nav.route && after && !nav.arrived && (
          <div className="flex items-center gap-2 bg-blue-900/70 px-3.5 py-1.5 text-[12px] text-blue-100">
            <span className="font-semibold">Depois</span>
            <ManeuverIcon step={after} className="h-4 w-4" />
            <span className="truncate">{after.instruction}</span>
          </div>
        )}
        {nav.recalculating && (
          <div className="relative flex items-center gap-2 overflow-hidden bg-amber-500 px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-950">
            <RotateCw className="h-3.5 w-3.5" style={{ animation: "avScan 1s linear infinite" }} />
            {nav.recalcReason === "vehicle" ? "Veículo se deslocou — recalculando rota" : "Recalculando rota"}
          </div>
        )}
      </div>
    </div>
  );
}

function NavMessage({ icon, title, text, tone }: { icon: ReactNode; title: string; text: string; tone: "amber" | "emerald" | "blue" }) {
  const bg = tone === "amber" ? "bg-amber-400 text-slate-950" : tone === "emerald" ? "bg-emerald-400 text-slate-950" : "bg-white/15";
  return (
    <div className="flex items-center gap-3">
      <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl ${bg}`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[17px] font-bold leading-tight">{title}</div>
        <div className="mt-0.5 text-[12.5px] text-blue-100">{text}</div>
      </div>
    </div>
  );
}

function NavBottom({ nav, data, now, onStop }: { nav: NavState; data: PublicLocationShare; now: number; onStop: () => void }) {
  const st = vehicleStatus(data, now);
  const v = data.vehicle;
  const pos = data.position;
  const eta = nav.remainingS != null ? new Date(now + nav.remainingS * 1000) : null;
  return (
    <div className="absolute inset-x-3 z-20" style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))", animation: "avSlideUp .3s ease-out both" }}>
      <div className="rounded-2xl border border-white/10 bg-slate-950/95 p-3.5 shadow-2xl backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="grid flex-1 grid-cols-3 gap-2 text-center">
            <NavStat label="Chegada" value={eta ? formatClock(eta, false) : "—"} highlight />
            <NavStat label="Tempo" value={nav.remainingS != null ? formatDuration(nav.remainingS) : "—"} />
            <NavStat label="Distância" value={nav.remainingM != null ? formatDistance(nav.remainingM) : "—"} />
          </div>
          <button
            type="button"
            onClick={onStop}
            aria-label="Encerrar navegação"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-red-600 text-white shadow-lg active:bg-red-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="mt-2.5 flex items-center gap-2 border-t border-white/5 pt-2.5 text-xs">
          {v?.plate && <Plate plate={v.plate} />}
          <span className="min-w-0 flex-1 truncate text-slate-300">
            {v?.name ?? "Veículo"}
            {" · "}
            <span className={st.tone === "emerald" ? "text-emerald-300" : st.tone === "amber" ? "text-amber-300" : "text-slate-400"}>
              {st.label}
              {pos?.moving && pos.speedKmh != null ? ` ${pos.speedKmh} km/h` : ""}
            </span>
          </span>
        </div>
        <div className="mt-1.5 text-[11px] text-slate-500">
          {nav.route?.trafficAware ? "Tempo considerando o trânsito atual. " : ""}
          {pos?.moving ? "A rota acompanha o veículo e é recalculada automaticamente." : "Rota recalculada automaticamente se você sair dela."}
        </div>
      </div>
    </div>
  );
}

function NavStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className={`text-[19px] font-black tabular-nums leading-none ${highlight ? "text-emerald-400" : "text-white"}`}>{value}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function ManeuverIcon({ step, className }: { step: LocationShareRouteStep; className?: string }) {
  const m = step.modifier ?? "straight";
  if (step.type === "arrive") return <Flag className={className} />;
  if (step.type === "roundabout" || step.type === "rotary" || step.type === "roundabout turn") return <RotateCw className={className} />;
  if (m === "uturn") return <Undo2 className={className} />;
  if (m === "right" || m === "sharp right") return <CornerUpRight className={className} />;
  if (m === "left" || m === "sharp left") return <CornerUpLeft className={className} />;
  if (m === "slight right") return <ArrowUpRight className={className} />;
  if (m === "slight left") return <ArrowUpLeft className={className} />;
  return <ArrowUp className={className} />;
}

// --- Marcadores -------------------------------------------------------------

// Só o carro no mapa (data.mapOnly): quadro da grade em perspectiva pelo
// rumo, nas mesmas cores de status do mapa. Sem grade cai no marcador comum.
const REALISTIC_SIZE = 72;
function RealisticVehicleMarker({ data, now }: { data: PublicLocationShare; now: number }) {
  const pos = data.position!;
  const stale = now - new Date(pos.fixTime).getTime() > STALE_MS;
  const tints = POLITRACKER_TINTS;
  const tint = pos.blocked ? tints.bloqueado : stale ? tints.offline : pos.moving ? tints.ligado : tints.parado;
  const sheet = resolveIsoSheet(data.iconKey ?? "hatchback", tint) ?? resolveIsoSheet("hatchback", tint);
  if (!sheet) return <VehicleMarker data={data} now={now} />;
  const { meta, url } = sheet;
  const frame = Number(isoFrameFor(meta, pos.course).slice(1));
  return (
    <div
      role="img"
      aria-label="Veículo"
      style={{
        width: REALISTIC_SIZE,
        height: REALISTIC_SIZE,
        transform: `translateY(${REALISTIC_SIZE / 2}px)`,
        backgroundImage: `url(${url})`,
        backgroundSize: `${meta.cols * REALISTIC_SIZE}px ${meta.rows * REALISTIC_SIZE}px`,
        backgroundPosition: `${-(frame % meta.cols) * REALISTIC_SIZE}px ${-Math.floor(frame / meta.cols) * REALISTIC_SIZE}px`,
      }}
    />
  );
}

function VehicleMarker({ data, now }: { data: PublicLocationShare; now: number }) {
  const pos = data.position!;
  const stale = now - new Date(pos.fixTime).getTime() > STALE_MS;
  const moving = pos.moving && !stale;
  const photo = data.vehicle?.photoUrl ? resolveGpsDriveUrl(data.vehicle.photoUrl) : null;
  const ring = stale ? "#94a3b8" : moving ? "#10b981" : "#ef4444";
  return (
    <div className="relative grid h-20 w-20 place-items-center" style={{ transform: "translateY(40px)" }}>
      {!stale &&
        [0, 1.2].map((d) => (
          <span
            key={d}
            className="absolute h-12 w-12 rounded-full"
            style={{ background: `${ring}55`, animation: `avPulse 2.4s ${d}s ease-out infinite` }}
          />
        ))}
      {moving && (
        <div className="absolute inset-0" style={{ transform: `rotate(${pos.course}deg)`, transition: "transform 1s ease" }}>
          <svg viewBox="0 0 20 14" className="absolute left-1/2 top-0.5 h-3.5 w-5 -translate-x-1/2 drop-shadow">
            <path d="M10 0 L20 14 L10 10 L0 14 Z" fill={ring} stroke="#fff" strokeWidth="1.5" />
          </svg>
        </div>
      )}
      <div
        className="relative grid h-12 w-12 place-items-center overflow-hidden rounded-full border-[3px] bg-slate-900 shadow-2xl"
        style={{ borderColor: ring }}
      >
        {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : <Navigation className="h-5 w-5 text-white" />}
      </div>
      {data.vehicle?.plate && (
        <span className="absolute -bottom-1 rounded bg-slate-950 px-1.5 py-px font-mono text-[10px] font-bold tracking-wider text-white shadow">
          {data.vehicle.plate.toUpperCase()}
        </span>
      )}
    </div>
  );
}

// Seta de navegação (pedido 2026-09-23: "ícone de seta invés de uma
// bolinha"). Direção: bússola/GPS do celular → último deslocamento → sentido
// da rota no ponto atual; sem nenhum dos três, aponta pro norte.
function GuestMarker({ heading }: { heading: number | null }) {
  const rot = useContinuousAngle(heading ?? 0);
  return (
    <div className="relative grid h-12 w-12 place-items-center" style={{ transform: "translateY(24px)" }}>
      <span className="absolute h-8 w-8 rounded-full bg-blue-500/30" style={{ animation: "avPulse 2s ease-out infinite" }} />
      <svg
        viewBox="0 0 40 40"
        className="relative h-10 w-10 drop-shadow-[0_2px_4px_rgba(0,0,0,.45)]"
        style={{ transform: `rotate(${rot}deg)`, transition: "transform .6s ease-out" }}
      >
        <path d="M20 3 L34 35 L20 27 L6 35 Z" fill="#2563eb" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// Evita a seta dar a volta inteira ao passar de 359° pra 0°.
function useContinuousAngle(deg: number): number {
  const ref = useRef(deg);
  const prev = ref.current;
  const delta = ((((deg - prev) % 360) + 540) % 360) - 180;
  ref.current = prev + delta;
  return ref.current;
}

// --- Câmera -----------------------------------------------------------------

const FOLLOW_RESUME_MS = 15_000;

function CameraController({
  mode,
  seq,
  vehicle,
  me,
  route,
  navigating,
  onModeChange,
}: {
  mode: CameraMode;
  seq: number;
  vehicle: LatLng | null;
  me: LatLng | null;
  route: LocationShareRoute | null;
  navigating: boolean;
  onModeChange: (mode: CameraMode) => void;
}) {
  const map = useGoogleMap();
  const lastPos = useRef<LatLng | null>(null);
  const dragging = useRef(false);
  const zoomingUntil = useRef(0);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resumeMode = useRef<CameraMode | null>(null);
  const follow = mode === "vehicle" ? vehicle : mode === "me" ? me : null;
  const followRef = useRef(follow);
  followRef.current = follow;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Troca de modo: ajusta zoom / enquadramento uma vez.
  useEffect(() => {
    if (!map) return;
    lastPos.current = null;
    if (mode !== "free") {
      clearTimeout(resumeTimer.current);
      resumeMode.current = null;
    }
    if (mode === "overview") {
      const b = new google.maps.LatLngBounds();
      if (vehicle) b.extend(vehicle);
      if (me) b.extend(me);
      if (navigating && route) for (const [lat, lng] of route.geometry) b.extend({ lat, lng });
      if (!b.isEmpty()) map.fitBounds(b, { top: 190, bottom: 260, left: 40, right: 70 });
    } else if (mode === "me" && me) {
      map.setZoom(17);
      map.moveCamera({ center: me });
    } else if (mode === "vehicle" && vehicle) {
      map.moveCamera({ center: vehicle });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, seq]);

  // Seguir (pedido 2026-11-01): moveCamera, não panTo — cada panTo reiniciava
  // a animação do anterior e o mapa ficava atrás do veículo. Anda o mapa o
  // mesmo tanto que o veículo, mantendo o enquadramento de quem arrastou.
  useEffect(() => {
    if (!map || !follow || dragging.current || performance.now() < zoomingUntil.current) return;
    const prev = lastPos.current;
    const center = map.getCenter();
    lastPos.current = follow;
    map.moveCamera({
      center: prev && center ? { lat: center.lat() + follow.lat - prev.lat, lng: center.lng() + follow.lng - prev.lng } : follow,
    });
  }, [map, follow]);

  // Mexer no mapa não para de seguir. Só pausa se o arraste tirou o veículo
  // da tela: volta depois de FOLLOW_RESUME_MS ou pelo botão de centralizar.
  useEffect(() => {
    if (!map) return;
    const inView = (p: LatLng | null) => !p || (map.getBounds()?.contains(p) ?? true);
    const onStart = map.addListener("dragstart", () => {
      dragging.current = true;
      clearTimeout(resumeTimer.current);
    });
    const onEnd = map.addListener("dragend", () => {
      dragging.current = false;
      const following = modeRef.current === "vehicle" || modeRef.current === "me";
      if (following && !inView(followRef.current)) {
        resumeMode.current = modeRef.current;
        onModeChange("free");
      }
      const back = resumeMode.current;
      if (back) resumeTimer.current = setTimeout(() => onModeChange(back), FOLLOW_RESUME_MS);
    });
    const onZoom = map.addListener("zoom_changed", () => {
      zoomingUntil.current = performance.now() + 500;
    });
    // Zoom pela posição dos dedos pode tirar o veículo da tela.
    const onIdle = map.addListener("idle", () => {
      const target = followRef.current;
      if (dragging.current || !target || inView(target)) return;
      lastPos.current = target;
      map.moveCamera({ center: target });
    });
    return () => {
      clearTimeout(resumeTimer.current);
      onStart.remove();
      onEnd.remove();
      onZoom.remove();
      onIdle.remove();
    };
  }, [map, onModeChange]);
  return null;
}

// --- Hooks de navegação -------------------------------------------------------

function useGuestGps(enabled: boolean) {
  const [fix, setFix] = useState<GuestFix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const lastMoved = useRef<{ at: LatLng; heading: number | null } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Este navegador não fornece localização.");
      return;
    }
    setLocating(true);
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setLocating(false);
        setError(null);
        const cur = { lat: p.coords.latitude, lng: p.coords.longitude };
        let heading = p.coords.heading != null && !Number.isNaN(p.coords.heading) && (p.coords.speed ?? 0) > 1 ? p.coords.heading : null;
        // Navegador sem heading (comum no iPhone/Chrome): usa o sentido do
        // último deslocamento real; parado, mantém a direção anterior.
        const prev = lastMoved.current;
        if (heading == null && prev) {
          heading = metersBetween(prev.at, cur) >= 8 ? bearing(prev.at, cur) : prev.heading;
        }
        if (!prev || metersBetween(prev.at, cur) >= 8) lastMoved.current = { at: cur, heading };
        else if (heading != null) lastMoved.current = { at: prev.at, heading };
        setFix({ ...cur, accuracy: p.coords.accuracy, heading, at: Date.now() });
      },
      (e) => {
        setLocating(false);
        setError(
          e.code === e.PERMISSION_DENIED
            ? "Acesso à localização negado. Libere nas configurações do navegador para traçar a rota."
            : "Não foi possível obter sua localização. Verifique se o GPS está ligado.",
        );
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);
  return { fix, error, locating };
}

interface Progress {
  segIndex: number;
  along: number;
  offRouteM: number;
  proj: LatLng;
}

function useNavigation(token: string, active: boolean, fix: GuestFix | null, target: LatLng | null) {
  const [route, setRoute] = useState<LocationShareRoute | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcReason, setRecalcReason] = useState<"offroute" | "vehicle" | null>(null);
  const inflight = useRef(false);
  const lastReqAt = useRef(0);
  const lastErrAt = useRef(0);
  const offCount = useRef(0);

  const cum = useMemo(() => (route ? cumulativeMeters(route.geometry) : null), [route]);
  const progress = useMemo<Progress | null>(
    () => (route && cum && fix ? projectOnRoute(route.geometry, cum, fix) : null),
    [route, cum, fix],
  );

  useEffect(() => {
    if (!active) {
      setRoute(null);
      setError(null);
      setRecalculating(false);
      offCount.current = 0;
    }
  }, [active]);

  const request = useCallback(
    async (from: LatLng, reason: "offroute" | "vehicle" | null) => {
      if (inflight.current) return;
      inflight.current = true;
      lastReqAt.current = Date.now();
      if (reason) {
        setRecalculating(true);
        setRecalcReason(reason);
      }
      try {
        const r = await gpsDriveLocationShareService.getPublicRoute(token, from);
        setRoute(r);
        setError(null);
        offCount.current = 0;
      } catch (err) {
        lastErrAt.current = Date.now();
        setError(err instanceof Error && err.message ? err.message : "Não foi possível calcular a rota.");
      } finally {
        inflight.current = false;
        setRecalculating(false);
      }
    },
    [token],
  );

  // Decide quando (re)calcular: sem rota, saiu da rota, veículo andou, rota
  // velha. Roda a cada novo fix do GPS e a cada nova posição do veículo.
  useEffect(() => {
    if (!active || !fix || !target) return;
    const t = Date.now();
    const sinceReq = t - lastReqAt.current;
    if (sinceReq < ROUTE_MIN_INTERVAL_MS) return;
    if (lastErrAt.current > lastReqAt.current - 1 && t - lastErrAt.current < ROUTE_RETRY_AFTER_ERROR_MS) return;
    if (metersBetween(fix, target) < ARRIVED_M) return;

    if (!route) {
      void request(fix, null);
      return;
    }
    if (progress && progress.offRouteM > Math.max(OFF_ROUTE_MIN_M, fix.accuracy * 1.5)) {
      offCount.current += 1;
      if (offCount.current >= 2) {
        void request(fix, "offroute");
        return;
      }
    } else {
      offCount.current = 0;
    }
    if (metersBetween(route.destination, target) > DEST_MOVED_M) {
      void request(fix, "vehicle");
      return;
    }
    if (t - new Date(route.calculatedAt).getTime() > ROUTE_MAX_AGE_MS && sinceReq > ROUTE_MAX_AGE_MS) {
      void request(fix, null);
    }
  }, [active, fix, target, route, progress, request]);

  let nextStep: LocationShareRouteStep | null = null;
  let afterStep: LocationShareRouteStep | null = null;
  let nextIndex = -1;
  let distToNextM: number | null = null;
  let remainingM: number | null = null;
  let remainingS: number | null = null;
  if (route && cum) {
    const along = progress?.along ?? 0;
    const seg = progress?.segIndex ?? -1;
    nextIndex = route.steps.findIndex((s) => s.geometryIndex > seg && s.type !== "depart");
    if (nextIndex === -1) nextIndex = route.steps.length - 1;
    nextStep = route.steps[nextIndex] ?? null;
    afterStep = route.steps[nextIndex + 1] ?? null;
    if (nextStep) distToNextM = Math.max(0, cum[Math.min(nextStep.geometryIndex, cum.length - 1)] - along);
    const total = cum[cum.length - 1] || 1;
    remainingM = Math.max(0, total - along);
    remainingS = Math.round(route.durationS * (remainingM / total));
  }
  const arrived = !!(fix && target && metersBetween(fix, target) < ARRIVED_M);

  return {
    route,
    error,
    recalculating,
    recalcReason,
    progress,
    nextStep,
    nextIndex,
    afterStep,
    distToNextM,
    remainingM,
    remainingS,
    arrived,
    hasFix: !!fix,
  };
}

// Voz pt-BR: anuncia a manobra quando ela vira a próxima (se ainda longe) e
// de novo quando está perto; avisa recálculo e chegada.
function useVoiceGuidance(enabled: boolean, nav: NavState) {
  const spoken = useRef<{ key: string; stage: "far" | "near" } | null>(null);
  const said = useRef({ recalc: false, arrived: false });
  const speak = useCallback(
    (text: string) => {
      if (!enabled || typeof window === "undefined" || !("speechSynthesis" in window)) return;
      try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(expandAbbreviations(text));
        u.lang = "pt-BR";
        u.rate = 1.05;
        window.speechSynthesis.speak(u);
      } catch {
        /* sem voz neste navegador */
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) spoken.current = null;
  }, [enabled]);

  useEffect(() => {
    if (nav.recalculating && !said.current.recalc) {
      said.current.recalc = true;
      speak("Recalculando rota");
    }
    if (!nav.recalculating) said.current.recalc = false;
  }, [nav.recalculating, speak]);

  useEffect(() => {
    if (nav.arrived && !said.current.arrived) {
      said.current.arrived = true;
      speak("Você chegou ao veículo");
    }
    if (!nav.arrived) said.current.arrived = false;
  }, [nav.arrived, speak]);

  const step = nav.nextStep;
  const dist = nav.distToNextM;
  const key = step && nav.route ? `${nav.route.calculatedAt}:${nav.nextIndex}` : "";
  useEffect(() => {
    if (!step || dist == null || nav.arrived) return;
    const cur = spoken.current;
    if (cur?.key !== key) {
      if (dist > 120) {
        spoken.current = { key, stage: "far" };
        speak(`Em ${spokenDistance(dist)}, ${lowerFirst(step.instruction)}`);
      } else {
        spoken.current = { key, stage: "near" };
        speak(step.instruction);
      }
    } else if (cur.stage === "far" && dist <= 60) {
      spoken.current = { key, stage: "near" };
      speak(step.instruction);
    }
  }, [key, dist, step, nav.arrived, speak]);
}

function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined") return;
    type WakeLockSentinelLike = { release: () => Promise<void> };
    const wl = (navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<WakeLockSentinelLike> } }).wakeLock;
    if (!wl) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;
    const acquire = () => {
      wl.request("screen")
        .then((s) => {
          if (cancelled) void s.release();
          else sentinel = s;
        })
        .catch(() => undefined);
    };
    acquire();
    const onVisible = () => {
      if (!document.hidden) acquire();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled]);
}

// --- Telas de estado ----------------------------------------------------------

function LoadingScreen({ error, onRetry }: { error: boolean; onRetry: () => void }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950 px-6 text-center">
      <img src={defaultLogoUrl} alt="GPS Drive" className="h-12 w-auto" />
      <div className="relative mt-8 h-24 w-24">
        <div className="absolute inset-0 rounded-full border border-emerald-500/30" />
        <div className="absolute inset-4 rounded-full border border-emerald-500/20" />
        {!error && (
          <div
            className="absolute inset-0 rounded-full"
            style={{ background: "conic-gradient(from 0deg, rgba(16,185,129,.45), transparent 25%)", animation: "avScan 1.6s linear infinite" }}
          />
        )}
        <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-400" />
      </div>
      <h1 className="mt-6 text-lg font-bold">{error ? "Sem conexão" : "Localizando veículo…"}</h1>
      <p className="mt-1 max-w-xs text-sm text-slate-400">
        {error ? "Não foi possível conectar ao servidor. Verifique sua internet." : "Estabelecendo conexão segura com o rastreador."}
      </p>
      {error && (
        <button type="button" onClick={onRetry} className="mt-5 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white">
          Tentar novamente
        </button>
      )}
      <div className="absolute inset-x-6" style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
        <BrandPhrase />
      </div>
    </div>
  );
}

function EndScreen({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950 px-6 text-center">
      <img src={defaultLogoUrl} alt="GPS Drive" className="absolute h-9 w-auto" style={{ top: "calc(1.5rem + env(safe-area-inset-top))" }} />
      <div className="grid h-20 w-20 place-items-center rounded-2xl bg-white/5 ring-1 ring-white/10">{icon}</div>
      <h1 className="mt-5 text-2xl font-bold">{title}</h1>
      <p className="mt-2 max-w-sm text-sm text-slate-400">{text}</p>
      <div className="absolute inset-x-6" style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
        <BrandPhrase />
      </div>
    </div>
  );
}

function NoPositionYet() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 px-6 text-center">
      <div className="relative h-20 w-20">
        <div className="absolute inset-0 rounded-full border border-slate-600" />
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: "conic-gradient(from 0deg, rgba(148,163,184,.35), transparent 25%)", animation: "avScan 2s linear infinite" }}
        />
      </div>
      <div className="mt-4 text-sm font-semibold text-slate-300">Aguardando a primeira posição do rastreador</div>
    </div>
  );
}

// --- helpers ---------------------------------------------------------------

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Desliza o marcador da posição antiga pra nova (~1,5s) em vez de "teleportar"
// a cada poll.
function useTweenedPosition(target: LatLng | null): LatLng | null {
  const [pos, setPos] = useState<LatLng | null>(target);
  const fromRef = useRef<LatLng | null>(target);
  const key = target ? `${target.lat},${target.lng}` : "";
  useEffect(() => {
    if (!target) return;
    const from = fromRef.current;
    if (!from || metersBetween(from, target) > 5000) {
      fromRef.current = target;
      setPos(target);
      return;
    }
    const start = performance.now();
    const dur = 1500;
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      const p = { lat: from.lat + (target.lat - from.lat) * e, lng: from.lng + (target.lng - from.lng) * e };
      fromRef.current = p;
      setPos(p);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return pos;
}

function useTweenedNumber(target: number): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / 900);
      const v = from + (target - from) * (1 - Math.pow(1 - k, 3));
      fromRef.current = v;
      setVal(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return val;
}

function metersBetween(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function bearing(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// Sentido da rota a partir do ponto atual (ignora vértices colados nele).
function routeBearing(path: LatLng[]): number | null {
  const next = path.find((p, i) => i > 0 && metersBetween(path[0], p) > 3);
  return next ? bearing(path[0], next) : null;
}

function cumulativeMeters(geom: [number, number][]): number[] {
  const out = [0];
  for (let i = 1; i < geom.length; i++) {
    out.push(out[i - 1] + metersBetween({ lat: geom[i - 1][0], lng: geom[i - 1][1] }, { lat: geom[i][0], lng: geom[i][1] }));
  }
  return out;
}

// Projeta o convidado no segmento mais próximo da rota (plano local em
// metros — suficiente pra distâncias de rua).
function projectOnRoute(geom: [number, number][], cum: number[], p: LatLng): Progress {
  const kx = Math.cos((p.lat * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  let best: Progress = { segIndex: 0, along: 0, offRouteM: Infinity, proj: { lat: geom[0][0], lng: geom[0][1] } };
  if (geom.length < 2) {
    best.offRouteM = metersBetween(p, best.proj);
    return best;
  }
  for (let i = 0; i < geom.length - 1; i++) {
    const ax = (geom[i][1] - p.lng) * kx;
    const ay = (geom[i][0] - p.lat) * ky;
    const bx = (geom[i + 1][1] - p.lng) * kx;
    const by = (geom[i + 1][0] - p.lat) * ky;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
    const px = ax + dx * t;
    const py = ay + dy * t;
    const d = Math.hypot(px, py);
    if (d < best.offRouteM) {
      best = {
        segIndex: i,
        along: cum[i] + (cum[i + 1] - cum[i]) * t,
        offRouteM: d,
        proj: { lat: p.lat + py / ky, lng: p.lng + px / kx },
      };
    }
  }
  return best;
}

function remainingGeometry(route: LocationShareRoute, progress: Progress): LatLng[] {
  const rest = route.geometry.slice(progress.segIndex + 1).map(([lat, lng]) => ({ lat, lng }));
  return [progress.proj, ...rest];
}

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.max(0, Math.round(m / 10) * 10)} m`;
  return `${(m / 1000).toFixed(m < 10_000 ? 1 : 0).replace(".", ",")} km`;
}

function spokenDistance(m: number): string {
  if (m < 1000) return `${Math.round(m / 50) * 50 || 50} metros`;
  const km = Math.round(m / 100) / 10;
  return `${String(km).replace(".", ",")} quilômetros`;
}

function formatDuration(s: number): string {
  const min = Math.max(1, Math.round(s / 60));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${String(m).padStart(2, "0")}`;
}

function formatClock(d: Date, withSeconds = true): string {
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", ...(withSeconds ? { second: "2-digit" } : {}) });
}

function formatAgo(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `há ${s}s`;
  const min = Math.round(s / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} dias`;
}

// Instruções do Google vêm abreviadas ("R.", "Av.", "Dr.") — a voz leria
// letra por letra.
const ABBREVIATIONS: [RegExp, string][] = [
  [/\bAv\.\s*(Av\.\s*)?/g, "Avenida "],
  [/\bR\.\s/g, "Rua "],
  [/\bPça\.\s/g, "Praça "],
  [/\bEstr\.\s/g, "Estrada "],
  [/\bRod\.\s/g, "Rodovia "],
  [/\bTv\.\s/g, "Travessa "],
  [/\bAl\.\s/g, "Alameda "],
  [/\bDr\.\s/g, "Doutor "],
  [/\bSen\.\s/g, "Senador "],
  [/\bPres\.\s/g, "Presidente "],
  [/\bGov\.\s/g, "Governador "],
  [/\bProf\.\s/g, "Professor "],
  [/\bEng\.\s/g, "Engenheiro "],
  [/\bCel\.\s/g, "Coronel "],
  [/\bSta\.\s/g, "Santa "],
  [/\bSto\.\s/g, "Santo "],
  [/\s—\s/g, ". "],
];
function expandAbbreviations(s: string): string {
  return ABBREVIATIONS.reduce((acc, [re, rep]) => acc.replace(re, rep), s);
}

function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}
